import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Tooltip, Collapse, Button, message } from 'antd';
import { useLazyQuery, useReactiveVar } from '@apollo/client';

import PropTypes from 'prop-types';
import copy from 'copy-to-clipboard';
import * as _ from 'lodash';
import moment from 'moment';

import CloseIcon from 'components/Icons/CloseIcon';
import ClosedMenuIcon from 'components/Icons/ClosedMenuIcon';
import InfoIcon from 'components/Icons/InfoIcon';
import RefreshIcon from 'components/Icons/RefreshIcon';
import helpers from 'helpers';
import utils from 'utils/utils';

import { formatLocale } from 'pages/Proposal/components/RichEditor/wix-components/wix-pricing-helper';
import { sanitizeString } from 'utils/xss';
import { ELEMENTS_VARIABLES, DEFAULT_TEMPLATE_PRICING } from 'constants/index';
import { userVar } from 'graphql/cache';
import { useProposalContext } from 'context/proposal';
import {
  REFRESH_PROPOSAL_PIPEDRIVE_DEAL,
  FETCH_HUBSPOT_DEAL_BY_ID,
  FETCH_HUBSPOT_COMPANY_BY_ID,
} from 'graphql/queries/integrationQueries';
import VariablesPanel from './VariablesPanel';

import '../ElementsMenu.scss';

const { Panel } = Collapse;

const SECTION_ELEMENTS_VARIABLES = JSON.parse(JSON.stringify(ELEMENTS_VARIABLES()));
delete SECTION_ELEMENTS_VARIABLES.custom;

const VariablesView = ({
  openVariablesList,
  showVariables,
  setShowVariables,
  setOpenVariablesList,
  isSection,
}) => {
  const {
    propRef,
    setProp,
    proposal,
    wixEditor,
    templateWixEditor,
    scrollingTo,
    setScrollingTo,
    saveTemplateProposal: saveProposal,
    savingDraftProposal,
    setSavingDraftProposal,
  } = useProposalContext();

  const variables = proposal?.draft?.variables;
  const language = proposal?.language?.toLowerCase() || 'english';
  const isRtl = utils.isRTL(language);
  const wixPricing = proposal?.wixPricing || {};
  const isSecondSignatureEnabled = proposal?.draft?.signature?.additionalSignature;
  const addDealVariables =
    (proposal?.pipedriveDealId ||
      proposal?.hubspot?.hubspotDealId ||
      proposal?.hubspot?.hubspotCompanyId ||
      proposal?.hubspot?.hubspotContactId) &&
    variables?.deal &&
    Object.keys(variables?.deal || {}).length > 0;
  const addCompanyVariables =
    (proposal?.hubspot?.hubspotDealId ||
      proposal?.hubspot?.hubspotCompanyId ||
      proposal?.hubspot?.hubspotContactId) &&
    variables?.company &&
    Object.keys(variables?.company || {}).length > 0;

  const user = useReactiveVar(userVar);
  const htmlInVariable = user?.adminFeatureFlags?.find(
    (flag) => flag.key === 'HTML_IN_VARIABLES'
  )?.enabled;

  const tempVariablesKeys = ['client', 'client2', 'proposal', 'sender'];
  if (addDealVariables) {
    tempVariablesKeys.push('deal');
  }
  if (addCompanyVariables) {
    tempVariablesKeys.push('company');
  }
  if (!isSection) {
    tempVariablesKeys.push('custom');
  }

  const [tempVariables, setTempVariables] = useState(
    isSection
      ? SECTION_ELEMENTS_VARIABLES
      : { ...ELEMENTS_VARIABLES(addDealVariables, addCompanyVariables), ...variables }
  );
  const [updateVariable, setUpdateVariable] = useState('');
  const [copiedVariable, setCopiedVariable] = useState('');
  const [openPopover, setOpenPopover] = useState({});
  const [errors, setErrors] = useState('');
  const [variableName, setVariableName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Ref for tracking data updates with expiry time
  const dataUpdated = useRef(null);

  const handleScroll = useCallback(
    (panelId) => {
      setTimeout(() => {
        const sectionEl = document.querySelector(`[id="${panelId}"]`);
        if (sectionEl) {
          sectionEl.scrollIntoView({ behavior: 'smooth' });
          sectionEl.classList.add('animate-simple-section');
          setTimeout(() => sectionEl.classList.remove('animate-simple-section'), 500);
          setScrollingTo('');
        }
      }, 500);
    },
    [setScrollingTo]
  );

  useEffect(() => {
    if (tempVariables?.length) {
      const dummyVariables =
        tempVariables ||
        variables ||
        ELEMENTS_VARIABLES(addDealVariables, addCompanyVariables) ||
        null;
      // dummyVariables.client.fullName = {
      //   count: dummyVariables?.client?.fullName?.count || 0,
      //   value:
      //     (dummyVariables?.client?.firstName?.value || '') +
      //     ' ' +
      //     (dummyVariables?.client?.lastName?.value || ''),
      // };
      // dummyVariables.sender.fullName = {
      //   count: dummyVariables?.sender?.fullName?.count || 0,
      //   value:
      //     (dummyVariables?.sender?.firstName?.value || '') +
      //     ' ' +
      //     (dummyVariables?.sender?.lastName?.value || ''),
      // };
      // dummyVariables.sender2.fullName = {
      //   count: dummyVariables?.sender2?.fullName?.count || 0,
      //   value:
      //     (dummyVariables?.sender2?.firstName?.value || '') +
      //     ' ' +
      //     (dummyVariables?.sender2?.lastName?.value || ''),
      // };

      if (dummyVariables) {
        setTempVariables(dummyVariables);
        handleScroll(scrollingTo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables, openVariablesList, showVariables, scrollingTo]);

  useEffect(() => {
    if (dataUpdated.current > Date.now() && updateVariable === 'updated') {
      // prevent data changing if its just updated
      setUpdateVariable('');
      return;
    }
    if (variables && !_.isEqual(variables, tempVariables)) {
      setTempVariables({ ...variables });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables]);

  const [refreshProposalPipedriveDeal] = useLazyQuery(REFRESH_PROPOSAL_PIPEDRIVE_DEAL, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ refreshProposalPipedriveDeal }) => {
      message.success('Pipedrive Deal Refreshed');

      propRef.current = { ...refreshProposalPipedriveDeal };
      setProp({ ...refreshProposalPipedriveDeal });
      setTempVariables({
        ...ELEMENTS_VARIABLES(addDealVariables, addCompanyVariables),
        ...refreshProposalPipedriveDeal.draft.variables,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const [refreshProposalHubspotDeal] = useLazyQuery(FETCH_HUBSPOT_DEAL_BY_ID, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ fetchHubSpotDealById }) => {
      // this will be variables only
      message.success('Hubspot Deal Refreshed');

      const tempProposalData = {
        ...propRef.current,
        draft: {
          ...propRef.current.draft,
          variables: {
            ...propRef.current.draft.variables,
            ...fetchHubSpotDealById,
          },
        },
      };
      propRef.current = { ...tempProposalData };
      setProp({ ...tempProposalData });
      setTempVariables({
        ...ELEMENTS_VARIABLES(addDealVariables, addCompanyVariables),
        ...tempProposalData.draft.variables,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const [refreshProposalHubspotCompany] = useLazyQuery(FETCH_HUBSPOT_COMPANY_BY_ID, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ fetchHubSpotCompanyById }) => {
      // this will be variables only
      message.success('Hubspot Company Refreshed');

      const tempProposalData = {
        ...propRef.current,
        draft: {
          ...propRef.current.draft,
          variables: {
            ...propRef.current.draft.variables,
            ...fetchHubSpotCompanyById,
          },
        },
      };
      propRef.current = { ...tempProposalData };
      setProp({ ...tempProposalData });
      setTempVariables({
        ...ELEMENTS_VARIABLES(addDealVariables, addCompanyVariables),
        ...tempProposalData.draft.variables,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const closeVariables = () => {
    setShowVariables(false);
    setOpenVariablesList(false);
  };

  // window.addEventListener('keydown', function (e) {
  //   if (e.code === 'Enter' || e.code === 'Escape') {
  //     setUpdateVariable('');
  //   }
  // });

  const saveProposalWithUpdatedVariable = (dummyVariables, custom) => {
    saveProposal({ 'draft.variables': dummyVariables || tempVariables });
    setScrollingTo('');

    if (custom) {
      setUpdateVariable('updated'); // 'updated' flag to prevent data changing in useEffect
      setVariableName('');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveVariableDebouce = useCallback(
    _.debounce((dummyVariables) => saveProposalWithUpdatedVariable(dummyVariables, true), 500),
    []
  );

  const createFullname = (data) => {
    return `${data?.firstName?.value} ${data?.lastName?.value}`.trim();
  };

  const onSaveVariable = (external, key, value, shouldSave = false) => {
    const newValue = htmlInVariable ? value : sanitizeString(value);
    if (!savingDraftProposal) {
      setSavingDraftProposal(true);
    }
    if (!shouldSave) {
      return newValue;
    }

    // save variable value
    let dummyVariables = JSON.parse(JSON.stringify(tempVariables));
    dummyVariables[external][key].value = newValue;

    if (external !== 'custom' && ['firstName', 'lastName'].includes(key)) {
      dummyVariables[external].fullName = {
        count: dummyVariables[external]?.fullName?.count || 0,
        value: createFullname(dummyVariables?.[external]),
      };
    }

    // Set dataUpdated flag with expiry time
    dataUpdated.current = Date.now() + 2000;

    setTempVariables(dummyVariables);
    saveVariableDebouce(dummyVariables);
  };

  const handleOpenChange = (variable) => {
    setOpenPopover({ ...openPopover, [variable]: !openPopover[variable] });
  };

  const getNext = (item) => {
    const customVariable = tempVariables.custom[`custom${item + 1}`];
    if (customVariable || customVariable === '') {
      return getNext(item + 1);
    }

    return item + 1;
  };

  const addCustomVariable = () => {
    let dummyVariables = JSON.parse(JSON.stringify(tempVariables));
    dummyVariables.custom[`custom${getNext(Object.keys(dummyVariables.custom).length)}`] = {
      value: '',
      count: 0,
    };
    saveProposal({ 'draft.variables': dummyVariables });
    setScrollingTo('');
  };

  const deleteCustomVariable = (variableName) => {
    let dummyVariables = JSON.parse(JSON.stringify(tempVariables));
    delete dummyVariables.custom[variableName];
    setOpenPopover({});
    saveProposal({ 'draft.variables': dummyVariables });
  };

  const copyVariable = (value, variable) => {
    setScrollingTo('');
    copy(`{{${value}.${variable}}}`);
    setCopiedVariable(`${value}.${variable}`);
    setTimeout(function () {
      setCopiedVariable('');
      setOpenPopover({ ...openPopover, [variable]: false });
    }, 5000);
  };

  const onRenameClick = (value, variable) => {
    setVariableName(variable);
    setUpdateVariable(updateVariable === `${value}.${variable}` ? '' : `${value}.${variable}`);
    setOpenPopover({});
  };

  const editCustomVariable = (newName, _variable) => {
    const alphacharsRegex = /^[A-Za-z_]+$/;
    if (newName.indexOf('.') > -1) {
      setErrorMessage('Dots(.) are not allowed and will be automatically change to underscore(_).');
      newName = newName.replaceAll('.', '_');
      setErrors(_variable);
    } else if (newName.indexOf('-') > -1) {
      setErrorMessage('Dash(-) are not allowed and will be automatically change to underscore(_).');
      newName = newName.replaceAll('-', '_');
      setErrors(_variable);
    } else if (newName.trim() === '') {
      setErrorMessage('Variable name cannot be empty.');
      setErrors(_variable);
    } else if (!alphacharsRegex.test(newName)) {
      setErrorMessage('Only English letters (A-Z, a-z) are allowed.');
      setErrors(_variable);
    } else {
      setErrors('');
      setErrorMessage('');
    }
    setVariableName(newName);
  };

  const saveCustomVariable = (e) => {
    const { code, ckey, keyCode } = e;

    if (code === 'Escape' || ckey === 'Escape' || keyCode === 27) {
      setErrors('');
      setUpdateVariable('');
      setVariableName('');
    } else if (code === 'Enter' || ckey === 'Enter' || keyCode === 13) {
      if (errorMessage === '') {
        // update variable
        const dummyVariables = JSON.parse(JSON.stringify(tempVariables));
        const newVariables = { ...dummyVariables };
        newVariables.custom = {};

        for (const oldVariable in dummyVariables.custom) {
          const oldValues = dummyVariables.custom[oldVariable];

          if (`custom.${oldVariable}` === updateVariable) {
            newVariables.custom[variableName] = oldValues;
          } else {
            newVariables.custom[oldVariable] = oldValues;
          }
        }

        setTempVariables(newVariables);
        saveProposalWithUpdatedVariable(newVariables, true);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.code === 'Escape' || e.key === 'Escape' || e.keyCode === 27) {
      setErrors('');
    } else if (e.code === 'Enter' || e.key === 'Enter' || e.keyCode === 13) {
      setErrors('');
      saveProposalWithUpdatedVariable();
    }
  };

  const transformVariableValue = (key, value) => {
    const datesValue = ['createdDate', 'sentDate', 'expireDate', 'createdAt'];

    const pricing = templateWixEditor && wixPricing ? DEFAULT_TEMPLATE_PRICING : wixPricing || {};

    const locale = formatLocale(pricing);
    const dateFormat = helpers.formatDateByRegion(locale);
    // transform dates
    if (datesValue.includes(key) && value?.length >= 9) {
      if (value.length === 9 || value.length === 10) {
        // NOTE this will not work for date 2023-04-15
        // convert 25-04-2023 to 2023-04-25 00:00:00
        value = `${value.split('-').reverse().join('-')} 00:00:00`;
      }
      const d = moment(value).format(
        templateWixEditor || wixEditor ? dateFormat : user?.profile?.dateFormat || 'DD/MM/YYYY'
      );

      return d === 'Invalid date' ? value : d;
    }

    return value;
  };

  const renderExtra = (value) => {
    if (value === 'deal') {
      return (
        <Tooltip
          title={proposal?.pipedriveDealId ? 'Refresh Pipedrive Deal' : 'Refresh Hubspot Deal'}>
          <RefreshIcon
            className="refresh-icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (proposal?.pipedriveDealId) {
                refreshProposalPipedriveDeal({ variables: { proposalId: proposal._id } });
              } else if (proposal?.hubspot?.hubspotDealId) {
                refreshProposalHubspotDeal({ variables: { proposalId: proposal._id } });
              }
            }}
          />
        </Tooltip>
      );
    } else if (value === 'company') {
      return (
        <Tooltip title="Refresh Hubspot Company">
          <RefreshIcon
            className="refresh-icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              refreshProposalHubspotCompany({ variables: { proposalId: proposal._id } });
            }}
          />
        </Tooltip>
      );
    }

    return '';
  };

  if (!showVariables) {
    return null;
  }

  return (
    <div className="elements-menu variables-view">
      <div className="head-variables">
        <div>
          <span className="left-text">Variables</span>
          {isSection && (
            <Tooltip
              title="Values of these added variables will be changed based on the proposal it's being used. You can set or change the values when editing your proposal."
              overlayClassName="elements-menu-tooltip">
              <span className="text-icon">
                <InfoIcon />
              </span>
            </Tooltip>
          )}
        </div>
        <span>
          <Button
            className="close-variables medium-btn grey-btn icon-right"
            onClick={closeVariables}
            icon={<CloseIcon className="close-variables-icon" />}
            override={true}>
            Close
          </Button>
        </span>
      </div>

      <Collapse
        defaultActiveKey={tempVariablesKeys}
        expandIcon={({ isActive }) => (
          <ClosedMenuIcon className={`${isActive ? 'opened' : ''} menu-icon`} />
        )}>
        {tempVariablesKeys.map((value) => {
          if (value === 'client2' && !isSecondSignatureEnabled) {
            return null;
          }
          return (
            <Panel
              header={utils.capitalizeFirstLetter(value)}
              key={value}
              id={value}
              extra={renderExtra(value)}>
              <VariablesPanel
                {...{
                  copiedVariable,
                  copyVariable,
                  updateVariable,
                  errors,
                  value,
                  errorMessage,
                  language,
                  variableName,
                  editCustomVariable,
                  saveCustomVariable,
                  isSection,
                  variables,
                  transformVariableValue,
                  handleKeyDown,
                  onSaveVariable,
                  htmlInVariable,
                  openPopover,
                  handleOpenChange,
                  addCustomVariable,
                  onRenameClick,
                  deleteCustomVariable,
                  isRtl,
                  tempPanelData: {
                    ...(SECTION_ELEMENTS_VARIABLES[value] || {}),
                    ...(tempVariables[value] || {}),
                  },
                }}
              />
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
};

VariablesView.propTypes = {
  variables: PropTypes.object,
  openVariablesList: PropTypes.bool.isRequired,
  showVariables: PropTypes.bool.isRequired,
  setShowVariables: PropTypes.func,
  setOpenVariablesList: PropTypes.func.isRequired,
};

export default VariablesView;
